// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect, useState, Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, LogOut, Mail, Phone, Calendar, DollarSign, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { SEO } from "../components/SEO";

const beginGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Admin() {
  const location = useLocation();
  const nav = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [tab, setTab] = useState("registrations");
  const [regs, setRegs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [clinWl, setClinWl] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [attDate, setAttDate] = useState("");
  const [attNote, setAttNote] = useState("");

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // let AuthCallback handle
    if (user) return;
    (async () => {
      try { const r = await api.get("/auth/me"); setUser(r.data); }
      catch { setUser(false); }
      finally { setChecking(false); }
    })();
  }, [user]);

  useEffect(() => {
    if (!user || !user.is_admin) return;
    setLoading(true);
    Promise.all([
      api.get("/admin/registrations"),
      api.get("/admin/contacts"),
      api.get("/admin/groups"),
      api.get("/admin/waitlist"),
      api.get("/admin/clinician-waitlist"),
      api.get("/admin/stats"),
    ]).then(([a, b, c, d, cw, e]) => { setRegs(a.data); setContacts(b.data); setGroups(c.data); setWaitlist(d.data); setClinWl(cw.data); setStats(e.data); })
      .finally(() => setLoading(false));
  }, [user]);

  const exportCsv = async (kind) => {
    const res = await api.get(`/admin/export/${kind}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${kind}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const runReminders = async () => {
    try {
      const r = await api.post("/admin/reminders/run");
      const { days_out, sent, reason } = r.data;
      if (reason) toast(`Retreat is ${days_out} days away — no reminder scheduled for this day.`);
      else toast.success(`Sent ${sent} reminder${sent === 1 ? "" : "s"} (T-minus ${days_out} days).`);
    } catch {
      toast.error("Unable to run reminders.");
    }
  };

  const refreshGroups = async () => {
    const c = await api.get("/admin/groups");
    setGroups(c.data);
  };

  const markAttended = async (regId) => {
    try {
      await api.post(`/admin/groups/${regId}/attend`, { date: attDate || null, note: attNote });
      setAttDate(""); setAttNote("");
      await refreshGroups();
      toast.success("Attendance recorded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to mark attendance");
    }
  };

  const removeAttended = async (regId, date) => {
    try {
      await api.post(`/admin/groups/${regId}/attend/remove`, { date });
      await refreshGroups();
      toast.success("Removed");
    } catch { toast.error("Unable to remove"); }
  };

  const notifyClinicianWaitlist = async (clinician) => {
    if (!window.confirm(`Email everyone on ${clinician}'s waitlist that spots are opening?`)) return;
    try {
      const r = await api.post("/admin/clinician-waitlist/notify", { clinician });
      toast.success(`Notified ${r.data.notified} on ${clinician}'s waitlist.`);
      const cw = await api.get("/admin/clinician-waitlist");
      setClinWl(cw.data);
    } catch { toast.error("Unable to send notifications."); }
  };

  const stripeBase = stats?.stripe_mode === "live" ? "https://dashboard.stripe.com" : "https://dashboard.stripe.com/test";
  const stripeSubUrl = (sid) => sid ? `${stripeBase}/subscriptions/${sid}` : null;

  const refreshRegs = async () => {
    const [a, s] = await Promise.all([api.get("/admin/registrations"), api.get("/admin/stats")]);
    setRegs(a.data); setStats(s.data);
  };

  const deleteRetreat = async (r) => {
    if (!window.confirm(`Delete ${r.first_name} ${r.last_name}'s registration? This also cancels their Stripe subscription. Refunds must be issued manually in Stripe.`)) return;
    try {
      await api.delete(`/admin/registrations/${r.id}`);
      await refreshRegs();
      toast.success("Registration deleted.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Unable to delete."); }
  };

  const deleteGroup = async (r) => {
    if (!window.confirm(`Delete ${r.first_name} ${r.last_name}'s ${r.group_title} enrollment?`)) return;
    try {
      await api.delete(`/admin/groups/${r.id}`);
      await refreshGroups();
      toast.success("Enrollment deleted.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Unable to delete."); }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    nav("/", { replace: true });
  };

  if (checking) return (
    <div className="min-h-screen flex items-center justify-center bg-cream">
      <SEO title="Admin | Beyond Blessed" description="Admin dashboard." path="/admin" noindex />
      <Loader2 className="animate-spin text-teal" size={36} />
    </div>
  );

  if (!user) return (
    <div data-testid="admin-login" className="min-h-screen flex items-center justify-center bg-cream pt-24 px-6">
      <SEO title="Admin | Beyond Blessed" description="Admin dashboard." path="/admin" noindex />
      <div className="max-w-md text-center bg-white p-10 border border-gold/25">
        <ShieldCheck className="text-teal mx-auto mb-5" size={44} />
        <h1 className="font-serif text-3xl text-navy mb-3">Admin Access</h1>
        <p className="text-charcoal/70 mb-8">Sign in with your Google account to manage registrations and contacts.</p>
        <button onClick={beginGoogleLogin} data-testid="google-login-btn" className="bg-navy text-cream px-8 py-4 rounded-full hover:bg-teal transition-colors inline-flex items-center gap-3 w-full justify-center">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google logo" className="h-5 w-5 bg-white rounded p-0.5" />
          Sign in with Google
        </button>
      </div>
    </div>
  );

  if (!user.is_admin) return (
    <div className="min-h-screen flex items-center justify-center bg-cream pt-24 px-6">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-3xl text-navy mb-3">Not authorized</h1>
        <p className="text-charcoal/70 mb-6">Your Google account ({user.email}) is not on the admin allowlist.</p>
        <button onClick={logout} className="border border-navy/30 px-6 py-3 rounded-full hover:bg-navy hover:text-cream transition">Sign out</button>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-dashboard" className="pt-28 pb-20 bg-cream min-h-screen">
      <SEO title="Admin | Beyond Blessed" description="Admin dashboard." path="/admin" noindex />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="uppercase tracking-[0.3em] text-xs text-teal mb-2">Admin</p>
            <h1 className="font-serif text-4xl md:text-5xl text-navy">Welcome back, {user.name?.split(" ")[0]}</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="border border-navy/20 px-5 py-2.5 rounded-full text-navy hover:bg-navy hover:text-cream transition inline-flex items-center gap-2">
            <LogOut size={16} /> Sign out
          </button>
        </div>

        <div className="flex items-center justify-end mb-4">
          <button onClick={runReminders} data-testid="admin-run-reminders" className="text-sm border border-teal/40 text-teal px-4 py-2 rounded-full hover:bg-teal hover:text-cream transition">
            Send retreat reminders now
          </button>
        </div>

        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
            {[
              ["Retreat Spots Left", `${stats.spots_remaining} / ${stats.spots_max}`],
              ["Retreat Registrations", stats.registrations],
              ["Group Enrollments", stats.groups],
              ["Waitlist", stats.waitlist ?? 0],
              ["Contacts", stats.contacts],
            ].map(([k, v]) => (
              <div key={k} className="bg-white p-6 border border-gold/25">
                <p className="text-charcoal/60 text-sm">{k}</p>
                <p className="font-serif text-4xl text-navy mt-1">{v}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-6 border-b border-gold/25 flex-wrap items-end justify-between">
          <div className="flex gap-2 flex-wrap">
            {[
              ["registrations", `Retreat (${regs.length})`],
              ["groups", `Groups (${groups.length})`],
              ["waitlist", `Retreat Waitlist (${waitlist.length})`],
              ["clinician_waitlist", `Clinician Waitlist (${clinWl.length})`],
              ["contacts", `Contacts (${contacts.length})`],
            ].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-teal text-teal" : "border-transparent text-navy/60 hover:text-navy"}`}>
                {l}
              </button>
            ))}
          </div>
          <button onClick={() => exportCsv(tab)} data-testid="admin-export-csv" className="text-sm border border-navy/20 px-4 py-2 rounded-full text-navy hover:bg-navy hover:text-cream transition mb-2">
            Export CSV
          </button>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="animate-spin text-teal mx-auto" size={30} /></div>
        ) : tab === "registrations" ? (
          <div className="bg-white border border-gold/25 overflow-x-auto" data-testid="admin-registrations">
            <table className="w-full text-sm">
              <thead className="bg-navy text-cream text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-4">Name</th>
                  <th className="text-left px-5 py-4">Email</th>
                  <th className="text-left px-5 py-4">Phone</th>
                  <th className="text-left px-5 py-4">Plan</th>
                  <th className="text-left px-5 py-4">Status</th>
                  <th className="text-left px-5 py-4">Progress</th>
                  <th className="text-left px-5 py-4">Created</th>
                  <th className="text-left px-5 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {regs.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-charcoal/50">No registrations yet.</td></tr>}
                {regs.map((r) => (
                  <tr key={r.id} className="border-t border-gold/10">
                    <td className="px-5 py-4 font-medium text-navy">{r.first_name} {r.last_name}</td>
                    <td className="px-5 py-4 text-charcoal/75"><a href={`mailto:${r.email}`} className="hover:text-teal">{r.email}</a></td>
                    <td className="px-5 py-4 text-charcoal/75">{r.phone}</td>
                    <td className="px-5 py-4 text-charcoal/75">{r.plan_label || r.plan}</td>
                    <td className="px-5 py-4"><StatusBadge s={r.payment_status} /></td>
                    <td className="px-5 py-4 text-charcoal/75">{r.installments_paid ?? 0} / {r.total_installments ?? 1}</td>
                    <td className="px-5 py-4 text-charcoal/50 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4 text-xs whitespace-nowrap">
                      {r.stripe_subscription_id && (
                        <a href={stripeSubUrl(r.stripe_subscription_id)} target="_blank" rel="noreferrer" data-testid={`refund-${r.id}`} className="text-teal underline mr-3 hover:text-navy">Refund in Stripe</a>
                      )}
                      <button onClick={() => deleteRetreat(r)} data-testid={`delete-reg-${r.id}`} className="text-red-500 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === "groups" ? (
          <div className="bg-white border border-gold/25 overflow-x-auto" data-testid="admin-groups">
            <table className="w-full text-sm">
              <thead className="bg-navy text-cream text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-4">Name</th>
                  <th className="text-left px-5 py-4">Email</th>
                  <th className="text-left px-5 py-4">Group</th>
                  <th className="text-left px-5 py-4">Plan</th>
                  <th className="text-left px-5 py-4">Status</th>
                  <th className="text-left px-5 py-4">Attended</th>
                  <th className="text-left px-5 py-4">Created</th>
                  <th className="text-left px-5 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-charcoal/50">No group enrollments yet.</td></tr>}
                {groups.map((r) => {
                  const att = r.attendance || [];
                  const expanded = expandedGroup === r.id;
                  return (
                    <Fragment key={r.id}>
                      <tr className="border-t border-gold/10">
                        <td className="px-5 py-4 font-medium text-navy">{r.first_name} {r.last_name}</td>
                        <td className="px-5 py-4 text-charcoal/75"><a href={`mailto:${r.email}`} className="hover:text-teal">{r.email}</a></td>
                        <td className="px-5 py-4 text-charcoal/75">{r.group_title}</td>
                        <td className="px-5 py-4 text-charcoal/75">{r.plan === "group_monthly" ? `Monthly (${att.length}/4)` : "Single"}</td>
                        <td className="px-5 py-4"><StatusBadge s={r.payment_status} /></td>
                        <td className="px-5 py-4">
                          <button onClick={() => { setExpandedGroup(expanded ? null : r.id); setAttDate(""); setAttNote(""); }} data-testid={`attend-toggle-${r.id}`} className="text-teal underline text-sm">
                            {att.length} session{att.length === 1 ? "" : "s"} {expanded ? "▲" : "▼"}
                          </button>
                        </td>
                        <td className="px-5 py-4 text-charcoal/50 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="px-5 py-4 text-xs whitespace-nowrap">
                          <button onClick={() => deleteGroup(r)} data-testid={`delete-group-${r.id}`} className="text-red-500 hover:underline">Delete</button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="bg-seafoam/10 border-t border-gold/10" data-testid={`attend-panel-${r.id}`}>
                          <td colSpan={8} className="px-5 py-6">
                            <p className="uppercase tracking-widest text-xs text-teal mb-3">Attendance history</p>
                            {att.length === 0 ? (
                              <p className="text-charcoal/50 text-sm mb-4">No sessions logged yet.</p>
                            ) : (
                              <ul className="space-y-1 mb-5 text-sm">
                                {att.map((a) => (
                                  <li key={a.date} className="flex items-center justify-between bg-white px-4 py-2 border border-gold/15">
                                    <span className="text-navy"><strong>{a.date}</strong>{a.note ? ` — ${a.note}` : ""}</span>
                                    <button onClick={() => removeAttended(r.id, a.date)} data-testid={`attend-remove-${r.id}-${a.date}`} className="text-red-500 text-xs hover:underline">Remove</button>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <div className="flex flex-wrap items-end gap-3">
                              <div className="flex flex-col">
                                <label className="text-xs uppercase tracking-widest text-charcoal/60 mb-1">Date (optional)</label>
                                <input type="date" value={attDate} onChange={(e) => setAttDate(e.target.value)} data-testid={`attend-date-${r.id}`} className="border border-gold/25 px-3 py-2 text-sm" />
                              </div>
                              <div className="flex flex-col grow min-w-[200px]">
                                <label className="text-xs uppercase tracking-widest text-charcoal/60 mb-1">Note (optional)</label>
                                <input type="text" value={attNote} onChange={(e) => setAttNote(e.target.value)} placeholder="e.g. Great engagement" data-testid={`attend-note-${r.id}`} className="border border-gold/25 px-3 py-2 text-sm" />
                              </div>
                              <button onClick={() => markAttended(r.id)} data-testid={`attend-mark-${r.id}`} className="bg-navy text-cream px-5 py-2.5 rounded-full text-sm hover:bg-teal transition">
                                {attDate ? "Mark attended" : "Mark attended today"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : tab === "waitlist" ? (
          <div className="bg-white border border-gold/25 overflow-x-auto" data-testid="admin-waitlist">
            <table className="w-full text-sm">
              <thead className="bg-navy text-cream text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-4">Name</th>
                  <th className="text-left px-5 py-4">Email</th>
                  <th className="text-left px-5 py-4">Phone</th>
                  <th className="text-left px-5 py-4">Joined</th>
                </tr>
              </thead>
              <tbody>
                {waitlist.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-charcoal/50">Nobody on the waitlist yet.</td></tr>}
                {waitlist.map((r) => (
                  <tr key={r.id} className="border-t border-gold/10">
                    <td className="px-5 py-4 font-medium text-navy">{r.first_name} {r.last_name}</td>
                    <td className="px-5 py-4 text-charcoal/75"><a href={`mailto:${r.email}`} className="hover:text-teal">{r.email}</a></td>
                    <td className="px-5 py-4 text-charcoal/75">{r.phone || "-"}</td>
                    <td className="px-5 py-4 text-charcoal/50 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === "clinician_waitlist" ? (
          <div data-testid="admin-clinician-waitlist">
            <div className="flex flex-wrap gap-3 mb-5">
              {["adrianne", "tanya"].map((c) => {
                const pending = clinWl.filter((w) => w.clinician === c && !w.notified_at).length;
                return (
                  <button key={c} onClick={() => notifyClinicianWaitlist(c)} data-testid={`notify-${c}`}
                    disabled={pending === 0}
                    className="bg-navy text-cream px-5 py-2.5 rounded-full text-sm hover:bg-teal transition disabled:opacity-50">
                    Notify {c[0].toUpperCase() + c.slice(1)}'s waitlist ({pending})
                  </button>
                );
              })}
            </div>
            <div className="bg-white border border-gold/25 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-navy text-cream text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-4">Clinician</th>
                    <th className="text-left px-5 py-4">Name</th>
                    <th className="text-left px-5 py-4">Email</th>
                    <th className="text-left px-5 py-4">Phone</th>
                    <th className="text-left px-5 py-4">Notes</th>
                    <th className="text-left px-5 py-4">Notified</th>
                    <th className="text-left px-5 py-4">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {clinWl.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-charcoal/50">No clinician waitlist entries yet.</td></tr>}
                  {clinWl.map((r) => (
                    <tr key={r.id} className="border-t border-gold/10">
                      <td className="px-5 py-4 text-navy capitalize font-medium">{r.clinician}</td>
                      <td className="px-5 py-4 text-charcoal/75">{r.first_name} {r.last_name}</td>
                      <td className="px-5 py-4 text-charcoal/75"><a href={`mailto:${r.email}`} className="hover:text-teal">{r.email}</a></td>
                      <td className="px-5 py-4 text-charcoal/75">{r.phone || "-"}</td>
                      <td className="px-5 py-4 text-charcoal/75 max-w-xs">{r.notes || "-"}</td>
                      <td className="px-5 py-4 text-charcoal/50 text-xs">{r.notified_at ? new Date(r.notified_at).toLocaleDateString() : "—"}</td>
                      <td className="px-5 py-4 text-charcoal/50 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gold/25 overflow-x-auto" data-testid="admin-contacts">
            <table className="w-full text-sm">
              <thead className="bg-navy text-cream text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-4">Name</th>
                  <th className="text-left px-5 py-4">Email</th>
                  <th className="text-left px-5 py-4">Phone</th>
                  <th className="text-left px-5 py-4">Subject</th>
                  <th className="text-left px-5 py-4">Message</th>
                  <th className="text-left px-5 py-4">Received</th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-charcoal/50">No contact messages yet.</td></tr>}
                {contacts.map((c) => (
                  <tr key={c.id} className="border-t border-gold/10 align-top">
                    <td className="px-5 py-4 font-medium text-navy">{c.name}</td>
                    <td className="px-5 py-4 text-charcoal/75"><a href={`mailto:${c.email}`} className="hover:text-teal inline-flex items-center gap-1"><Mail size={13} /> {c.email}</a></td>
                    <td className="px-5 py-4 text-charcoal/75">{c.phone || "-"}</td>
                    <td className="px-5 py-4 text-charcoal/75">{c.subject || "-"}</td>
                    <td className="px-5 py-4 text-charcoal/75 max-w-md">{c.message}</td>
                    <td className="px-5 py-4 text-charcoal/50 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const StatusBadge = ({ s }) => {
  const map = {
    paid_in_full: ["Paid in Full", "bg-teal text-cream"],
    installment_active: ["On Plan", "bg-goldbright text-navy"],
    pending: ["Pending", "bg-navy/10 text-navy"],
  };
  const [label, cls] = map[s] || [s || "—", "bg-navy/10 text-navy"];
  return <span className={`text-xs px-3 py-1 rounded-full ${cls}`}>{label}</span>;
};
